import { Dispatch, SetStateAction, useState, useMemo, useEffect } from "react";

import { Queue, useSearchElasticStats } from "@coherehealth/qm-api";
import config from "api/config";
import {
  DateFilterOptions,
  ChartData,
  CASE_HISTORICAL_PRODUCTION_DATE_FILTER_OPTIONS,
  VisualizationDateFilter,
  defaultSkill,
} from "../util/ChartUtil";
import {
  historicalQueueProductionQueryGenerator,
  caseChartDataMapper,
  getQueueFilterClausesWithoutFaxAutoAttach,
} from "../util/QueryUtil";
import DueDatesOverTimeChart from "../util/charts/DueDatesOverTimeChart";
import { makeStyles, createStyles, Theme } from "@material-ui/core/styles";
import { Card, H2, SecondarySingleSelectDropdown } from "@coherehealth/common";

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    chartContainer: {
      height: 202,
      margin: "24px -30px 0px -16px",
    },
    chartTitleItem: {
      marginRight: 36,
    },
    chartCard: {
      margin: "32px 0px",
      padding: "16px 24px",
    },
  })
);

interface QueueHistoricalProductionChartProps {
  queue: Queue;
  dateValue: DateFilterOptions;
  setHasError: Dispatch<SetStateAction<boolean>>;
  changeQueryStringValue: (newDateValue: DateFilterOptions, skill?: string) => void;
  skillsOptions: { id: string; label: string }[];
  skillValue: string;
  setSkillValue: Dispatch<SetStateAction<string>>;
}

const QueueVolumeHistoricalProductionChartContent = ({
  queue,
  dateValue,
  setHasError,
  changeQueryStringValue,
  skillsOptions,
  skillValue,
  setSkillValue,
}: QueueHistoricalProductionChartProps) => {
  const classes = useStyles();
  const [data, setData] = useState<ChartData>([]);

  const { mutate: searchElasticStats, error } = useSearchElasticStats({
    base: `${config.QM_SERVICE_API_URL}`,
  });

  const { filters, filterTree, useFilterTree } = queue;
  const queueBasedSearchPayload = useMemo(() => {
    const skill = skillValue === defaultSkill.id ? undefined : skillValue;
    return historicalQueueProductionQueryGenerator(
      dateValue,
      getQueueFilterClausesWithoutFaxAutoAttach({ filters, filterTree, useFilterTree }),
      skill ? [skill] : undefined
    );
  }, [dateValue, filters, filterTree, useFilterTree, skillValue]);

  useEffect(() => {
    const fetchStatsData = async () => {
      const res = await searchElasticStats(queueBasedSearchPayload);
      const aggregatedData = caseChartDataMapper(res.aggregations?.dateCompleted, dateValue, true);
      setData(aggregatedData);
    };

    fetchStatsData();
  }, [searchElasticStats, queueBasedSearchPayload, dateValue]);

  useEffect(() => {
    if (error) {
      setHasError(true);
    }
  }, [error, setHasError]);

  const isQueueSkillBased = useMemo(() => {
    const faxIndex = filters?.findIndex((filter) => filter.filterValue === "FAX_INTAKE");
    return faxIndex ? faxIndex < 0 : true;
  }, [filters]);

  return (
    <>
      <div style={{ display: "flex", justifyContent: "flex-start", margin: "57px 0px -8px" }}>
        <H2 className={classes.chartTitleItem} style={{ width: 233 }}>
          Historical Production
        </H2>
        <div className={classes.chartTitleItem}>
          <VisualizationDateFilter
            dateValue={dateValue}
            setDateValue={changeQueryStringValue}
            options={CASE_HISTORICAL_PRODUCTION_DATE_FILTER_OPTIONS}
            historical
          />
        </div>
        {isQueueSkillBased && (
          <div className={classes.chartTitleItem}>
            <SecondarySingleSelectDropdown
              options={skillsOptions}
              value={skillValue}
              onChange={(val) => {
                setSkillValue(val);
                const newSkill = val === defaultSkill.id ? "All" : val;
                changeQueryStringValue(dateValue, newSkill);
              }}
              label="Filter By:"
              style={{ width: 150 }}
              maxMenuHeight={600}
            />
          </div>
        )}
      </div>
      <Card className={classes.chartCard}>
        <div className={classes.chartContainer}>
          <DueDatesOverTimeChart data={data} yLabel="Cases worked" hourly={dateValue === "1"} />
        </div>
      </Card>
    </>
  );
};

export default QueueVolumeHistoricalProductionChartContent;
